<script setup lang="ts">
import { FetchError } from 'ofetch'
import { VForm } from 'vuetify/components/VForm'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type { TableOptions } from '@core/stores/user'
import { useUserStore } from '@core/stores/user'
import type { Options, SortItem } from '@core/types'
import type { App } from '@/types/generated'
import { Modules } from '@/types/generated'

type Entry = Modules.Notification.Data.EmailStopListEntryData
type CheckResult = Modules.Notification.Data.EmailStopListCheckData
type UnblockData = Modules.Notification.Data.EmailStopListUnblockData
type DomainSuggestion = Modules.Notification.Data.EmailStopListDomainSuggestionData
type EntryType = Modules.Notification.Enums.EmailStopListEntryTypeEnum
type Reason = Modules.Notification.Enums.EmailStopListReasonEnum
type UnblockResult = Modules.Notification.Enums.EmailStopListUnblockResultEnum
type FieldErrors = Partial<Record<string, string[]>>

interface EntriesPage {
  data: Entry[]
  total: number
}

interface UnblockOutcome {
  type: 'success' | 'info' | 'warning' | 'error'
  message: string
}

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()
const userStore = useUserStore()

const { EmailStopListEntryTypeEnum, EmailStopListReasonEnum, EmailStopListUnblockResultEnum } = Modules.Notification.Enums

const table = 'email-stop-list'

const UNBLOCK_OUTCOME_STATUSES = [409, 502]

const headers = [
  { title: t('emailStopList.headers.type'), key: 'type' },
  { title: t('emailStopList.headers.value'), key: 'value' },
  { title: t('emailStopList.headers.reason'), key: 'reason' },
  { title: t('emailStopList.headers.providerReply'), key: 'providerReply', sortable: false },
  { title: t('emailStopList.headers.note'), key: 'note', sortable: false },
  { title: t('emailStopList.headers.author'), key: 'createdByName', sortable: false },
  { title: t('emailStopList.headers.createdAt'), key: 'created_at', value: 'createdAt' },
  { title: t('emailStopList.headers.blockedCount'), key: 'blocked_count', value: 'blockedCount' },
  { title: t('emailStopList.headers.lastBlockedAt'), key: 'last_blocked_at', value: 'lastBlockedAt' },
  { title: '', key: 'actions', sortable: false },
]

const typeTitles: Record<EntryType, string> = {
  [EmailStopListEntryTypeEnum.Address]: t('emailStopList.type.address'),
  [EmailStopListEntryTypeEnum.Domain]: t('emailStopList.type.domain'),
}

const reasonTitles: Record<Reason, string> = {
  [EmailStopListReasonEnum.Manual]: t('emailStopList.reason.manual'),
  [EmailStopListReasonEnum.ProviderRejected]: t('emailStopList.reason.providerRejected'),
}

const reasonOptions = Object.values(EmailStopListReasonEnum)
  .map(reason => ({ value: reason, title: reasonTitles[reason] }))

const userData = useCookie<App.Http.Controllers.Auth.UserData>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

const { locale } = useI18n({ useScope: 'global' })
const dateLocale = getDateLocale(locale.value)

const formatDateTime = (value: string) => formatDate(
  value,
  { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: userTimeZone.value },
  dateLocale,
)

const requestFailedMessage = () => t('emailStopList.errors.requestFailed')

const toFieldErrors = (error: unknown): FieldErrors => {
  const errors: unknown = error instanceof FetchError && error.statusCode === 422 ? error.data?.errors : undefined

  if (!errors || typeof errors !== 'object')
    return { '': [requestFailedMessage()] }

  return Object.fromEntries(
    Object.entries(errors).map(([field, messages]) => [field, [messages].flat().map(String)]),
  )
}

const pageError = ref('')
const isPageErrorVisible = ref(false)

const showPageError = (message: string) => {
  pageError.value = message
  isPageErrorVisible.value = true
}

const defaultTableOptions: TableOptions = {
  sortBy: 'created_at',
  orderBy: 'desc',
  page: 1,
  perPage: 10,
}

const tableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const reasonFilter = ref<Reason | null>(null)
const itemsPerPage = ref(tableOptions.value.perPage)
const page = ref(tableOptions.value.page)
const sortBy = ref(tableOptions.value.sortBy)
const orderBy = ref(tableOptions.value.orderBy)

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const updateOptions = (options: Options) => {
  page.value = options.page

  const [sortItem] = options.sortBy

  sortBy.value = sortItem?.key ?? defaultTableOptions.sortBy
  orderBy.value = sortItem?.order ?? defaultTableOptions.orderBy
}

const { data: entriesData, execute: fetchEntries, isFetching } = await useApi<EntriesPage>(createUrl(`/admin/${table}`, {
  query: {
    search: debouncedSearch,
    reason: computed(() => reasonFilter.value ?? undefined),
    page,
    itemsPerPage,
    sortBy,
    orderBy,
  },
}))

const { data: suggestionsData, execute: fetchSuggestions } = await useApi<DomainSuggestion[]>(`/admin/${table}/domain-suggestions`)

const entries = computed(() => entriesData.value?.data ?? [])
const total = computed(() => entriesData.value?.total ?? 0)
const suggestions = computed(() => suggestionsData.value ?? [])

const refreshAll = () => Promise.all([fetchEntries(), fetchSuggestions()])

watch([debouncedSearch, reasonFilter], () => {
  page.value = 1
})

watch([page, itemsPerPage, sortBy, orderBy], ([newPage, newItemsPerPage, newSortBy, newOrderBy], [, oldItemsPerPage]) => {
  if (newItemsPerPage !== oldItemsPerPage)
    page.value = 1

  tableOptions.value = {
    page: newPage,
    perPage: newItemsPerPage,
    sortBy: newSortBy,
    orderBy: newOrderBy,
  }
})

const isAddDialogVisible = ref(false)
const addForm = ref<VForm>()
const newEntry = ref({ value: '', note: '' })
const addErrors = ref<FieldErrors>({})
const isAdding = ref(false)

const openAddDialog = () => {
  newEntry.value = { value: '', note: '' }
  addErrors.value = {}
  isAddDialogVisible.value = true
}

const submitAdd = async () => {
  const { valid } = await addForm.value!.validate()
  if (!valid)
    return

  isAdding.value = true
  addErrors.value = {}
  try {
    await $api<Entry>(`/admin/${table}`, { method: 'POST', body: newEntry.value })
    isAddDialogVisible.value = false
    await refreshAll()
  }
  catch (error) {
    addErrors.value = toFieldErrors(error)
  }
  finally {
    isAdding.value = false
  }
}

const isNoteDialogVisible = ref(false)
const noteTarget = ref<Entry | null>(null)
const noteText = ref('')
const noteErrors = ref<FieldErrors>({})
const isSavingNote = ref(false)

const openNoteDialog = (entry: Entry) => {
  noteTarget.value = entry
  noteText.value = entry.note ?? ''
  noteErrors.value = {}
  isNoteDialogVisible.value = true
}

const submitNote = async () => {
  const entry = noteTarget.value
  if (entry === null)
    return

  isSavingNote.value = true
  noteErrors.value = {}
  try {
    await $api<Entry>(`/admin/${table}/${entry.id}`, { method: 'PATCH', body: { note: noteText.value } })
    isNoteDialogVisible.value = false
    await fetchEntries()
  }
  catch (error) {
    noteErrors.value = toFieldErrors(error)
  }
  finally {
    isSavingNote.value = false
  }
}

const isUnblockConfirmVisible = ref(false)
const isUnblockResultVisible = ref(false)
const unblockTarget = ref<Entry | null>(null)
const unblockResult = ref<UnblockData | null>(null)
const isUnblocking = ref(false)

const openUnblockConfirm = (entry: Entry) => {
  unblockTarget.value = entry
  isUnblockConfirmVisible.value = true
}

const unblock = async () => {
  const entry = unblockTarget.value
  if (entry === null)
    return

  unblockResult.value = null
  isUnblockResultVisible.value = true
  isUnblocking.value = true
  try {
    // 409 is an outcome here, and $api retries 409 by default: each retry would query the mail service again.
    unblockResult.value = await $api<UnblockData>(`/admin/${table}/${entry.id}`, { method: 'DELETE', retry: 0 })
  }
  catch (error) {
    if (error instanceof FetchError && UNBLOCK_OUTCOME_STATUSES.includes(error.statusCode ?? 0) && error.data?.result) {
      unblockResult.value = error.data as UnblockData

      // utils/api.ts raises the global error snackbar on any 5xx, but 502 here is a reported outcome.
      userStore.responseHasError = false
    }
    else {
      isUnblockResultVisible.value = false
      showPageError(requestFailedMessage())
    }
  }
  finally {
    isUnblocking.value = false
  }
  await refreshAll()
}

const unblockOutcome = computed((): UnblockOutcome | null => {
  if (unblockResult.value === null)
    return null

  const outcomes: Record<UnblockResult, UnblockOutcome> = {
    [EmailStopListUnblockResultEnum.RemovedEverywhere]: {
      type: 'success',
      message: t('emailStopList.unblock.result.removedEverywhere'),
    },
    [EmailStopListUnblockResultEnum.RemovedLocally]: {
      type: 'success',
      message: t('emailStopList.unblock.result.removedLocally'),
    },
    [EmailStopListUnblockResultEnum.ProviderNotConfigured]: {
      type: 'info',
      message: t('emailStopList.unblock.result.providerNotConfigured'),
    },
    [EmailStopListUnblockResultEnum.ProviderUnavailable]: {
      type: 'error',
      message: t('emailStopList.unblock.result.providerUnavailable'),
    },
    [EmailStopListUnblockResultEnum.StillListedAtProvider]: {
      type: 'warning',
      message: t('emailStopList.unblock.result.stillListedAtProvider'),
    },
  }

  return outcomes[unblockResult.value.result]
})

const isStillListedAtProvider = computed(
  () => unblockResult.value?.result === EmailStopListUnblockResultEnum.StillListedAtProvider,
)

const isBlockDomainConfirmVisible = ref(false)
const domainTarget = ref<DomainSuggestion | null>(null)

const openBlockDomainConfirm = (suggestion: DomainSuggestion) => {
  domainTarget.value = suggestion
  isBlockDomainConfirmVisible.value = true
}

const blockDomain = async () => {
  const suggestion = domainTarget.value
  if (suggestion === null)
    return

  try {
    await $api<Entry>(`/admin/${table}`, { method: 'POST', body: { value: suggestion.domain } })
  }
  catch (error) {
    showPageError(Object.values(toFieldErrors(error)).flatMap(messages => messages ?? []).join(' '))
  }
  await refreshAll()
}

const checkForm = ref<VForm>()
const checkEmail = ref('')
const checkResult = ref<CheckResult | null>(null)
const checkErrors = ref<FieldErrors>({})
const isChecking = ref(false)

const submitCheck = async () => {
  const { valid } = await checkForm.value!.validate()
  if (!valid)
    return

  isChecking.value = true
  checkErrors.value = {}
  try {
    checkResult.value = await $api<CheckResult>(`/admin/${table}/check`, { method: 'POST', body: { email: checkEmail.value } })
  }
  catch (error) {
    checkResult.value = null
    checkErrors.value = toFieldErrors(error)
  }
  finally {
    isChecking.value = false
  }
}

const platformCheck = computed(() => {
  const entry = checkResult.value?.platformEntry ?? null

  return entry === null
    ? { color: 'success', text: t('emailStopList.check.notListed') }
    : { color: 'error', text: t('emailStopList.check.platformListed', { value: entry.value }) }
})

const providerCheck = computed(() => {
  const result = checkResult.value
  if (result === null)
    return null

  if (!result.providerConfigured)
    return { color: 'secondary', text: t('emailStopList.check.providerNotConfigured') }

  if (!result.providerAvailable)
    return { color: 'warning', text: t('emailStopList.check.providerUnavailable') }

  return result.providerListed
    ? { color: 'error', text: t('emailStopList.check.listed') }
    : { color: 'success', text: t('emailStopList.check.notListed') }
})
</script>

<template>
  <div>
    <VCard
      class="mb-6"
      :title="t('emailStopList.check.title')"
      :subtitle="t('emailStopList.check.hint')"
    >
      <VCardText>
        <VForm
          ref="checkForm"
          :disabled="isChecking"
          @submit.prevent="submitCheck"
        >
          <div class="d-flex flex-wrap align-start gap-4">
            <AppTextField
              v-model="checkEmail"
              :placeholder="t('emailStopList.check.placeholder')"
              :rules="[requiredValidator]"
              :error-messages="checkErrors.email"
              class="flex-grow-1"
              style="min-inline-size: 240px;"
              maxlength="255"
              @update:model-value="checkErrors = {}"
            />
            <VBtn
              type="submit"
              prepend-icon="tabler-search"
              :loading="isChecking"
              :disabled="isChecking"
            >
              {{ t('emailStopList.check.submit') }}
            </VBtn>
          </div>
          <VAlert
            v-if="checkErrors['']"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            <div
              v-for="(message, index) in checkErrors['']"
              :key="index"
            >
              {{ message }}
            </div>
          </VAlert>
        </VForm>

        <div
          v-if="checkResult"
          class="d-flex flex-column gap-4 mt-6"
        >
          <div class="text-body-2">
            {{ t('emailStopList.check.resultFor', { email: checkResult.email }) }}
          </div>
          <div>
            <div class="text-body-2 text-disabled">
              {{ t('emailStopList.check.platform') }}
            </div>
            <div :class="`text-${platformCheck.color} font-weight-medium`">
              {{ platformCheck.text }}
            </div>
          </div>
          <div>
            <div class="text-body-2 text-disabled">
              {{ t('emailStopList.check.provider') }}
            </div>
            <div
              v-if="providerCheck"
              :class="`text-${providerCheck.color} font-weight-medium`"
            >
              {{ providerCheck.text }}
            </div>
            <template v-if="checkResult.providerListed && checkResult.providerReasons.length">
              <div class="text-body-2 mt-2">
                {{ t('emailStopList.check.providerReasons') }}
              </div>
              <ul class="ps-6">
                <li
                  v-for="(reason, index) in checkResult.providerReasons"
                  :key="index"
                  class="text-body-2"
                >
                  {{ reason }}
                </li>
              </ul>
            </template>
          </div>
        </div>
      </VCardText>
    </VCard>

    <VAlert
      v-for="suggestion in suggestions"
      :key="suggestion.domain"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      <div class="d-flex flex-wrap align-center gap-4">
        <span class="flex-grow-1">
          {{ t('emailStopList.suggestions.text', { domain: suggestion.domain, count: suggestion.count }) }}
        </span>
        <VBtn
          size="small"
          prepend-icon="tabler-ban"
          @click="openBlockDomainConfirm(suggestion)"
        >
          {{ t('emailStopList.suggestions.block') }}
        </VBtn>
      </div>
    </VAlert>

    <VCard
      :title="t('emailStopList.title')"
      :subtitle="t('emailStopList.description')"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex flex-wrap gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('emailStopList.searchPlaceholder')"
            style=" min-inline-size: 240px;"
          />
          <AppSelect
            v-model="reasonFilter"
            :items="reasonOptions"
            :placeholder="t('emailStopList.allReasons')"
            clearable
            style=" min-inline-size: 220px;"
          />
        </div>
        <VSpacer />
        <VBtn
          prepend-icon="tabler-plus"
          @click="openAddDialog"
        >
          {{ t('emailStopList.add') }}
        </VBtn>
      </VCardText>

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :sort-by="sortTable"
        :loading="isFetching"
        :loading-text="t('loading')"
        :no-data-text="t('emailStopList.empty')"
        :items="entries"
        :items-length="total"
        item-value="id"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.type="{ item }">
          {{ typeTitles[item.type] }}
        </template>
        <template #item.value="{ item }">
          <span class="font-weight-medium">{{ item.value }}</span>
        </template>
        <template #item.reason="{ item }">
          {{ reasonTitles[item.reason] }}
        </template>
        <template #item.providerReply="{ item }">
          <span
            class="text-xs"
            style="display: block; max-inline-size: 240px; white-space: normal;"
          >{{ item.providerReply ?? '—' }}</span>
        </template>
        <template #item.note="{ item }">
          <span
            class="text-xs"
            style="display: block; max-inline-size: 240px; white-space: normal;"
          >{{ item.note ?? '—' }}</span>
        </template>
        <template #item.createdByName="{ item }">
          {{ item.createdByName ?? t('emailStopList.author.system') }}
        </template>
        <template #item.created_at="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>
        <template #item.last_blocked_at="{ item }">
          {{ item.lastBlockedAt ? formatDateTime(item.lastBlockedAt) : '—' }}
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn
              :title="t('emailStopList.editNote')"
              @click="openNoteDialog(item)"
            >
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              :title="t('emailStopList.unblock.action')"
              @click="openUnblockConfirm(item)"
            >
              <VIcon icon="tabler-lock-open" />
            </IconBtn>
          </div>
        </template>
        <template #bottom>
          <div>
            <VDivider />

            <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
              <AppSelect
                v-model="itemsPerPage"
                density="compact"
                class="app-select flex-grow-0"
                :items="[5, 10, 25, 50]"
                style="max-inline-size: 5rem;min-inline-size: 5rem;"
              />

              <VPagination
                v-model="page"
                active-color="primary"
                :length="Math.ceil(total / itemsPerPage)"
                :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
              />
            </div>
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <VDialog
      v-model="isAddDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 600"
      :persistent="isAdding"
    >
      <DialogCloseBtn
        :disabled="isAdding"
        @click="isAddDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('emailStopList.addTitle') }}
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="addForm"
            :disabled="isAdding"
            @submit.prevent="submitAdd"
          >
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="newEntry.value"
                  :label="t('emailStopList.fields.value')"
                  :hint="t('emailStopList.fields.valueHint')"
                  :rules="[requiredValidator]"
                  :error-messages="addErrors.value"
                  persistent-hint
                  maxlength="255"
                  @update:model-value="addErrors.value = undefined"
                />
              </VCol>
              <VCol cols="12">
                <AppTextarea
                  v-model="newEntry.note"
                  :label="t('emailStopList.fields.note')"
                  :error-messages="addErrors.note"
                  rows="3"
                  auto-grow
                  maxlength="2000"
                />
              </VCol>
              <VCol
                v-if="addErrors['']"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in addErrors['']"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isAdding"
                  :disabled="isAdding"
                >
                  {{ t('emailStopList.add') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isAdding"
                  @click="isAddDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VDialog
      v-model="isNoteDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 600"
      :persistent="isSavingNote"
    >
      <DialogCloseBtn
        :disabled="isSavingNote"
        @click="isNoteDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('emailStopList.editNote') }}
          </VCardTitle>
          <VCardSubtitle v-if="noteTarget">
            {{ noteTarget.value }}
          </VCardSubtitle>
        </VCardItem>

        <VCardText>
          <VForm
            :disabled="isSavingNote"
            @submit.prevent="submitNote"
          >
            <VRow>
              <VCol cols="12">
                <AppTextarea
                  v-model="noteText"
                  :label="t('emailStopList.fields.note')"
                  :error-messages="noteErrors.note"
                  rows="3"
                  auto-grow
                  maxlength="2000"
                />
              </VCol>
              <VCol
                v-if="noteErrors['']"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in noteErrors['']"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isSavingNote"
                  :disabled="isSavingNote"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isSavingNote"
                  @click="isNoteDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VDialog
      v-model="isUnblockResultVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 600"
      :persistent="isUnblocking"
    >
      <DialogCloseBtn
        :disabled="isUnblocking"
        @click="isUnblockResultVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('emailStopList.unblock.resultTitle') }}
          </VCardTitle>
          <VCardSubtitle v-if="unblockTarget">
            {{ unblockTarget.value }}
          </VCardSubtitle>
        </VCardItem>

        <VCardText v-if="isUnblocking">
          <VProgressLinear
            indeterminate
            color="primary"
          />
        </VCardText>

        <VCardText v-else-if="unblockOutcome">
          <VAlert
            :type="unblockOutcome.type"
            variant="tonal"
          >
            {{ unblockOutcome.message }}
          </VAlert>

          <div
            v-if="isStillListedAtProvider"
            class="mt-4"
          >
            <VBtn
              v-if="unblockResult?.providerPanelUrl"
              :href="unblockResult.providerPanelUrl"
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              append-icon="tabler-external-link"
            >
              {{ t('emailStopList.unblock.openPanel') }}
            </VBtn>
            <div
              v-else
              class="text-body-2"
            >
              {{ t('emailStopList.unblock.panelPath') }}
            </div>
          </div>

          <div class="d-flex flex-wrap justify-center gap-4 mt-6">
            <VBtn
              v-if="isStillListedAtProvider"
              prepend-icon="tabler-refresh"
              @click="unblock"
            >
              {{ t('emailStopList.unblock.retry') }}
            </VBtn>
            <VBtn
              color="secondary"
              variant="tonal"
              @click="isUnblockResultVisible = false"
            >
              {{ t('close') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:isDialogVisible="isUnblockConfirmVisible"
      :confirmation-question="unblockTarget ? t('emailStopList.unblock.confirm', { value: unblockTarget.value }) : ''"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="unblock"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isBlockDomainConfirmVisible"
      :confirmation-question="domainTarget ? t('emailStopList.suggestions.confirm', { domain: domainTarget.domain }) : ''"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="blockDomain"
    />

    <VSnackbar
      v-model="isPageErrorVisible"
      color="error"
    >
      {{ pageError }}
    </VSnackbar>
  </div>
</template>
